import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { CompanyApplication } from '@core/models/dynamic-company.models';

@Injectable({
  providedIn: 'root'
})
export class BrandingService {
  private readonly titleService = inject(Title);
  private readonly document = inject(DOCUMENT);

  // Default branding when company is not found
  private readonly defaultTitle = 'The Operator OS';
  private readonly defaultFavicon = 'favicon.ico';

  /**
   * Initialize branding based on current company from localStorage
   */
  initializeBranding(): void {
    const currentCompanyName = this.getCurrentCompanyName();
    const companyData = this.getCompanyData(currentCompanyName);

    if (companyData) {
      this.applyBranding(companyData);
    } else {
      // Apply default branding if no company found
      this.updateTitle(this.defaultTitle);
      this.updateFavicon(this.defaultFavicon);
    }
  }

  /**
   * Get current company name from localStorage
   */
  private getCurrentCompanyName(): string {
    return localStorage.getItem('currentCompany') || '';
  }

  /**
   * Get company data from Applications in localStorage
   */
  private getCompanyData(companyName: string): CompanyApplication | null {
    try {
      const applicationsJson = localStorage.getItem('Applications');
      if (!applicationsJson) return null;

      const applications: CompanyApplication[] = JSON.parse(applicationsJson);

      // Find matching company (case-insensitive)
      const company = applications.find(
        app => app.name.toLowerCase() === companyName.toLowerCase()
      );

      return company || null;
    } catch (error) {
      console.error('Error loading company data:', error);
      return null;
    }
  }

  // Fallback images for known companies (same as company-switcher)
  private readonly fallbackImages: Record<string, string> = {
    unity: 'assets/images/unity-icon.webp',
    mingle: 'assets/headers-image/mingle-icon.svg'
  };

  /**
   * Apply branding for a specific company
   */
  applyBranding(company: CompanyApplication): void {
    // Use displayName for title, fallback to name
    const title = company.displayName || company.name || this.defaultTitle;
    this.updateTitle(title);

    // Use logo for favicon, then fallback images, then default
    const favicon = company.logo || this.getFallbackImage(company.name) || this.defaultFavicon;
    this.updateFavicon(favicon);

    console.log(`🎨 Branding applied for company:`, {
      name: company.name,
      title,
      favicon
    });
  }

  /**
   * Get fallback image for known companies
   */
  private getFallbackImage(companyName: string): string | null {
    return this.fallbackImages[companyName.toLowerCase()] || null;
  }

  /**
   * Update the document title
   */
  private updateTitle(title: string): void {
    this.titleService.setTitle(title);
  }

  /**
   * Update the favicon
   */
  private updateFavicon(faviconPath: string): void {
    if (!faviconPath) {
      faviconPath = this.defaultFavicon;
    }

    // Find existing favicon link element
    let link: HTMLLinkElement | null = this.document.querySelector("link[rel~='icon']");

    if (!link) {
      // Create new favicon link if it doesn't exist
      link = this.document.createElement('link');
      link.rel = 'icon';
      this.document.head.appendChild(link);
    }

    // Update favicon type based on extension
    link.type = faviconPath.endsWith('.svg') ? 'image/svg+xml' :
                faviconPath.endsWith('.png') ? 'image/png' :
                faviconPath.endsWith('.webp') ? 'image/webp' : 'image/x-icon';
    link.href = faviconPath;
  }

  /**
   * Get all available companies
   */
  getAvailableCompanies(): CompanyApplication[] {
    try {
      const applicationsJson = localStorage.getItem('Applications');
      if (!applicationsJson) return [];

      const applications: CompanyApplication[] = JSON.parse(applicationsJson);
      return applications.filter(app => app.isAvailable);
    } catch (error) {
      console.error('Error loading companies:', error);
      return [];
    }
  }

  /**
   * Get current company data
   */
  getCurrentCompany(): CompanyApplication | null {
    const companyName = this.getCurrentCompanyName();
    return this.getCompanyData(companyName);
  }
}
